import {
    getLoanRepresentativeExample,
    getLoanRepresentativeExampleOptions,
} from '@ilg-totallymoney/cardloanutils'

import { ScoredLoanProduct } from '@appTypes/PagesApi.interface'

import { defaultSortKey, getSortComparator } from './getSortComparator'
import { truncateToOneDecimalPlace } from './truncateToOneDecimalPlace'

type LoanEventData = {
    Apr: string
    ChanceOfAcceptance: string
    HasGuaranteedApr: boolean
    ImageUrl: string
    LoanId: string
    MatchFactor: number
    MonthlyPayment: number
    Name: string
    RepExampleText: string
}

type IndexedLoanEventData<T, Index extends number> = {
    [K in keyof T & string as `Loan${Index}${K}`]: T[K]
}

interface TopLoansEventData extends IndexedLoanEventData<
    Partial<LoanEventData>,
    1 | 2 | 3
> {
    NumberOfEligibleProducts: number
}

const getLoanEventData = <DisplayIndex extends number>(
    loan: ScoredLoanProduct,
    displayIndex: DisplayIndex
): IndexedLoanEventData<LoanEventData, DisplayIndex> => {
    const loanPrefixKey = `Loan${displayIndex}` as const
    const productDetails = loan.productDetails

    const result = {
        [`${loanPrefixKey}Name`]: `${productDetails.lender} - ${productDetails.loanName}`,
        [`${loanPrefixKey}ImageUrl`]: productDetails.imageUrl,
        [`${loanPrefixKey}Lender`]: productDetails.lender,
        [`${loanPrefixKey}LoanId`]: productDetails.loanId,
        [`${loanPrefixKey}LoanName`]: productDetails.loanName,
        [`${loanPrefixKey}MonthlyPayment`]: loan.monthlyPayment?.toFixed(2),
        [`${loanPrefixKey}MatchFactor`]: loan.recommenderScore,
        [`${loanPrefixKey}ChanceOfAcceptance`]: `${loan.score}%`,
        [`${loanPrefixKey}RepExampleText`]: getLoanRepresentativeExample(
            getLoanRepresentativeExampleOptions(loan),
            { isScored: true }
        ),
        [`${loanPrefixKey}Apr`]: truncateToOneDecimalPlace(loan.apr ?? 0),
        [`${loanPrefixKey}HasGuaranteedApr`]: loan.hasGuaranteedApr,
        [`${loanPrefixKey}HasMarketingOffer`]: loan.hasMarketingOffer,
    }

    return result as any
}

const getTopLoansEventData = (
    loans: ScoredLoanProduct[] | null,
    isNewAglo?: boolean
) => {
    if (loans == null) {
        return null
    }

    const sortedScoredLoans = loans
        .filter(
            loan =>
                !loan.counterOffer &&
                loan.productDetails.type !== 'Guarantor' &&
                loan.score &&
                loan.score > 0
        )
        .sort(
            getSortComparator(
                isNewAglo ? 'recommenderScoreRanking' : defaultSortKey
            )
        )

    if (sortedScoredLoans.length > 0) {
        const topThreeLoans = sortedScoredLoans.slice(0, 3)

        const eventData = {
            NumberOfEligibleProducts: sortedScoredLoans.length,
        } as TopLoansEventData

        return topThreeLoans.reduce((data, loan, index) => {
            return {
                ...data,
                ...getLoanEventData(loan, index + 1),
            }
        }, eventData)
    }

    return null
}

export default getTopLoansEventData
